import React, { useEffect, useRef, useMemo } from 'react';
import { X, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { UserAvatar } from '../common/UserAvatar';

interface ReactionDetailsPopupProps {
  emoji: string;
  count: number;
  userIds: string[];
  hasReacted: boolean;
  onClose: () => void;
  onToggleReaction?: () => void;
}

export const ReactionDetailsPopup: React.FC<ReactionDetailsPopupProps> = ({
  emoji,
  count,
  userIds = [],
  hasReacted,
  onClose,
  onToggleReaction,
}) => {
  const { user: currentUser } = useAuth();
  const { members } = useWorkspace();
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Resolve user identities from workspace members and auth state
  const resolvedUsers = useMemo(() => {
    const list: Array<{ id: string; displayName: string; avatarUrl?: string | undefined; isSelf: boolean }> = [];
    const seenIds = new Set<string>();

    for (const uid of userIds) {
      if (seenIds.has(uid)) continue;
      seenIds.add(uid);

      if (currentUser && currentUser.id === uid) {
        list.push({
          id: currentUser.id,
          displayName: currentUser.displayName,
          avatarUrl: currentUser.avatarUrl,
          isSelf: true,
        });
      } else {
        const member = members.find((m) => m.user.id === uid);
        if (member) {
          list.push({
            id: member.user.id,
            displayName: member.user.displayName,
            avatarUrl: member.user.avatarUrl,
            isSelf: false,
          });
        } else {
          list.push({
            id: uid,
            displayName: `User ${uid.slice(0, 6)}`,
            isSelf: false,
          });
        }
      }
    }

    // If hasReacted is true but current user is not in userIds array, ensure current user is listed
    if (hasReacted && currentUser && !seenIds.has(currentUser.id)) {
      list.unshift({
        id: currentUser.id,
        displayName: currentUser.displayName,
        avatarUrl: currentUser.avatarUrl,
        isSelf: true,
      });
    }

    return list;
  }, [userIds, members, currentUser, hasReacted]);

  return (
    <div
      ref={popupRef}
      role="dialog"
      aria-label={`Users who reacted with ${emoji}`}
      className="absolute bottom-full mb-2 left-0 w-72 max-w-[90vw] bg-card border border-border/80 rounded-2xl shadow-2xl z-50 overflow-hidden text-card-foreground select-none animate-in fade-in zoom-in-95 duration-150"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border/60 bg-muted/30">
        <div className="flex items-center gap-2">
          <span className="text-xl leading-none">{emoji}</span>
          <span className="text-xs font-bold text-foreground">{count} {count === 1 ? 'reaction' : 'reactions'}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* User List */}
      <div className="max-h-60 overflow-y-auto p-2 space-y-1 divide-y divide-border/30">
        {resolvedUsers.length > 0 ? (
          resolvedUsers.map((u) => (
            <div
              key={u.id}
              className="flex items-center justify-between px-2.5 py-1.5 rounded-xl hover:bg-muted/50 transition pt-2"
            >
                <div className="flex items-center gap-2.5 min-w-0">
                  <UserAvatar
                    userId={u.id}
                    displayName={u.displayName}
                    avatarUrl={u.avatarUrl}
                    size="xs"
                    className="shadow-2xs flex-shrink-0"
                  />
                  <span className="text-xs font-medium text-foreground truncate">
                    {u.displayName}
                  </span>
                </div>

                {u.isSelf && (
                  <span className="text-[10px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-md flex-shrink-0">
                    You
                  </span>
                )}
              </div>
          ))
        ) : (
          <div className="py-6 text-center text-xs text-muted-foreground">
            <p>{count} {count === 1 ? 'person' : 'people'} reacted</p>
          </div>
        )}
      </div>

      {/* Footer / Toggle Action */}
      {onToggleReaction && (
        <div className="p-2 border-t border-border/50 bg-muted/20">
          <button
            type="button"
            onClick={() => {
              onToggleReaction();
              onClose();
            }}
            className={`w-full py-1.5 px-3 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-1.5 ${
              hasReacted
                ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {hasReacted ? (
              <>
                <X className="h-3.5 w-3.5" /> Remove your reaction
              </>
            ) : (
              <>
                <Check className="h-3.5 w-3.5" /> React with {emoji}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
